import { useScrollReveal } from '@/hooks/useScrollReveal';

const tiers = [
  {
    badge: 'Verified',
    icon: 'ri-shield-check-line',
    color: 'primary',
    tierNum: '01',
    desc: 'Every partner on the Freshhlock network passes KYC verification and a baseline facility audit before they can list capacity, vehicles, or products.',
    checks: ['KYC-verified entity', 'Facility or vehicle inspection', 'Minimum insurance coverage', 'Operational history review'],
  },
  {
    badge: 'HACCP-Certified',
    icon: 'ri-award-line',
    color: 'secondary',
    tierNum: '02',
    desc: 'Partners handling food products must demonstrate HACCP compliance — temperature logs, hygiene protocols, pest control, and staff training documented and periodically audited.',
    checks: ['HACCP documentation verified', 'Temperature log audit trail', 'Hygiene & pest control protocols', 'Staff food-safety training records'],
  },
  {
    badge: 'Export-Grade',
    icon: 'ri-global-line',
    color: 'accent',
    tierNum: '03',
    desc: 'The highest tier. Facilities meeting international export standards — tested for residue limits, packaging compliance, and cold-chain integrity at every handoff point.',
    checks: ['International residue compliance', 'Export packaging standards', 'End-to-end cold-chain logging', 'Quarterly third-party audit'],
  },
];

const pillarTrust = [
  {
    pillar: 'Storage',
    icon: 'ri-building-2-line',
    stat: '340+',
    statLabel: 'Verified Warehouses',
    desc: 'Every warehouse listed is audited and certified before any buyer books capacity.',
    color: 'primary',
  },
  {
    pillar: 'Transport',
    icon: 'ri-truck-line',
    stat: '2,500+',
    statLabel: 'Verified Vehicles',
    desc: 'Every vehicle is verified, temperature-logging is mandatory, and driver credentials are checked.',
    color: 'secondary',
  },
  {
    pillar: 'Wholesale',
    icon: 'ri-exchange-line',
    stat: '8,000+',
    statLabel: 'Verified SKUs',
    desc: 'Every SKU carries a published spec sheet. If the product doesn\'t match, the trade doesn\'t clear.',
    color: 'accent',
  },
  {
    pillar: 'Retail',
    icon: 'ri-shopping-bag-3-line',
    stat: '100%',
    statLabel: 'Order Guarantee',
    desc: 'Every delivery is cold-chain logged. If the temperature breaks, the order is replaced — no questions asked.',
    color: 'foreground',
  },
];

export default function TrustSystemSection() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="relative py-16 md:py-24 bg-background-50 overflow-hidden">
      {/* Decorative grid background */}
      <div className="absolute inset-0 opacity-[0.025] pointer-events-none" style={{
        backgroundImage: `linear-gradient(to right, oklch(var(--accent-400)) 1px, transparent 1px), linear-gradient(to bottom, oklch(var(--accent-400)) 1px, transparent 1px)`,
        backgroundSize: '48px 48px',
      }}></div>

      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''} relative z-10`}>
        {/* Section header */}
        <div className="text-center mb-14 md:mb-18">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100/70 border border-accent-200/50 mb-4">
            <span className="text-xs font-semibold text-accent-700 uppercase tracking-wider">Our Trust System</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-700 mb-4">
            Trust That&apos;s Built In, Not Bolted On
          </h2>
          <p className="text-foreground-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            In a traditional fresh market, quality is a guess — you inspect what you can, and you hope. Freshhlock replaces guesswork with standardised specifications, verified certifications, and full traceability across every link in the chain.
          </p>
        </div>

        {/* Trust across all five pillars */}
        <div className="mb-16 md:mb-20">
          <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-800 text-center mb-10">
            Trust Runs Through Every Pillar
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {pillarTrust.map((p) => {
              const accent = {
                primary: { bar: 'bg-primary-500', light: 'bg-primary-50', border: 'border-primary-200', iconBg: 'bg-primary-100', iconText: 'text-primary-600', statColor: 'text-primary-700', hoverBorder: 'hover:border-primary-300/60' },
                secondary: { bar: 'bg-secondary-500', light: 'bg-secondary-50', border: 'border-secondary-200', iconBg: 'bg-secondary-100', iconText: 'text-secondary-600', statColor: 'text-secondary-700', hoverBorder: 'hover:border-secondary-300/60' },
                accent: { bar: 'bg-accent-500', light: 'bg-accent-50', border: 'border-accent-200', iconBg: 'bg-accent-100', iconText: 'text-accent-600', statColor: 'text-accent-700', hoverBorder: 'hover:border-accent-300/60' },
                foreground: { bar: 'bg-foreground-500', light: 'bg-foreground-50', border: 'border-foreground-200', iconBg: 'bg-foreground-100', iconText: 'text-foreground-600', statColor: 'text-foreground-800', hoverBorder: 'hover:border-foreground-300/60' },
              }[p.color]!;

              return (
                <div key={p.pillar} className={`group relative bg-card-50 rounded-xl border ${accent.border} ${accent.hoverBorder} p-5 md:p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default card-animate`}>
                  {/* Top accent bar */}
                  <div className={`absolute top-0 left-5 right-5 h-1 rounded-full ${accent.bar} opacity-25 group-hover:opacity-50 transition-opacity`}></div>

                  {/* Stat */}
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className={`font-heading text-2xl md:text-3xl font-bold ${accent.statColor} tabular-nums`}>
                      {p.stat}
                    </span>
                    <span className="text-xs text-foreground-400 font-medium">{p.statLabel}</span>
                  </div>

                  {/* Icon */}
                  <div className={`w-11 h-11 rounded-xl ${accent.iconBg} flex items-center justify-center mb-3 animate-icon-float icon-hover-pop`}>
                    <i className={`${p.icon} text-lg ${accent.iconText}`}></i>
                  </div>

                  {/* Title */}
                  <div className="font-heading text-sm font-semibold text-primary-700 mb-2">{p.pillar}</div>

                  {/* Description */}
                  <p className="text-xs text-foreground-500 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Certification tiers */}
        <div>
          <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-800 text-center mb-10">
            Three Certification Tiers. One Standard of Trust.
          </h3>

          <div className="relative max-w-5xl mx-auto">
            {/* Connecting flow line (desktop) */}
            <div className="hidden lg:block absolute top-20 left-[18%] right-[18%] h-[2px]">
              <div className="relative w-full h-full">
                <div className="absolute left-0 top-0 w-[45%] h-full bg-gradient-to-r from-primary-400/50 via-primary-400/30 to-secondary-400/30"></div>
                <div className="absolute right-0 top-0 w-[45%] h-full bg-gradient-to-l from-accent-400/50 via-accent-400/30 to-secondary-400/30"></div>
                {/* Flow dots */}
                <div className="absolute left-1/4 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary-400/60"></div>
                <div className="absolute left-1/2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-secondary-400/60"></div>
                <div className="absolute left-3/4 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-accent-400/60"></div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {tiers.map((tier) => {
                const isPrimary = tier.color === 'primary';
                const isSecondary = tier.color === 'secondary';
                const isAccent = tier.color === 'accent';

                const barColor = isPrimary ? 'bg-brand-500' : isSecondary ? 'bg-secondary-500' : 'bg-accent-500';
                const badgeBg = isPrimary ? 'bg-brand-600' : isSecondary ? 'bg-secondary-600' : 'bg-accent-500';
                const badgeText = isAccent ? 'text-brand-800' : 'text-paper-50';
                const iconColor = isPrimary ? 'text-primary-600' : isSecondary ? 'text-secondary-600' : 'text-accent-600';
                const iconBg = isPrimary ? 'bg-primary-100' : isSecondary ? 'bg-secondary-100' : 'bg-accent-100';
                const borderColor = isPrimary ? 'border-primary-200/60 hover:border-primary-400/60' : isSecondary ? 'border-secondary-200/60 hover:border-secondary-400/60' : 'border-accent-200/60 hover:border-accent-400/60';
                const checkColor = isPrimary ? 'text-primary-500' : isSecondary ? 'text-secondary-500' : 'text-accent-500';
                const statAccent = isPrimary ? 'text-primary-400/40' : isSecondary ? 'text-secondary-400/40' : 'text-accent-400/40';

                return (
                  <div key={tier.badge} className={`group relative bg-card-50 rounded-xl border ${borderColor} p-6 md:p-7 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 cursor-default card-animate`}>
                    {/* Top accent bar - full width */}
                    <div className={`absolute top-0 left-5 right-5 h-1.5 rounded-full ${barColor} opacity-30 group-hover:opacity-60 transition-opacity`}></div>

                    {/* Tier number watermark */}
                    <div className={`absolute top-5 right-5 font-heading text-5xl font-black ${statAccent} select-none pointer-events-none`}>
                      {tier.tierNum}
                    </div>

                    {/* Badge */}
                    <div className={`relative z-10 inline-flex items-center gap-2 px-3 py-1 rounded-full ${badgeBg} ${badgeText} text-xs font-semibold mb-5`}>
                      {tier.badge}
                    </div>

                    {/* Icon */}
                    <div className={`relative z-10 w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center mb-4 animate-icon-float icon-hover-pop`}>
                      <i className={`${tier.icon} text-xl ${iconColor}`}></i>
                    </div>

                    {/* Description */}
                    <p className="relative z-10 text-sm text-foreground-600 leading-relaxed mb-6">{tier.desc}</p>

                    {/* Checklist */}
                    <ul className="relative z-10 space-y-2.5">
                      {tier.checks.map((c) => (
                        <li key={c} className="flex items-start gap-2.5 text-xs text-foreground-500 group/check">
                          <div className={`w-5 h-5 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0 mt-px group-hover/check:scale-110 transition-transform duration-200`}>
                            <i className={`ri-check-line ${checkColor} text-xs`}></i>
                          </div>
                          <span className="pt-0.5">{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}